import './ActualVsPredicted.scss';
import { capitalize } from 'inflected';
import { ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  TooltipPayloadEntry,
  XAxis,
  YAxis
} from 'recharts';
import {
  dateStringToUtcTimestamp,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { TrainTestData } from '@blisspointmedia/bpm-types/dist/Forecasting';
import { Brand20, Brand60, Neutral400, Neutral500 } from '../../../utils/colors';
import { dateToMddyy } from '../../../utils/date-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '../ChartConstants';
import {
  ChartTooltipV2,
  KeyShapeOptions,
  TooltipPassthroughProps
} from '../ChartTooltip/ChartTooltipV2';
import { headerFormatter, useDynamicYAxisWidth } from '../ChartUtils';
import { useBuildMonthlyXAxis } from '../MonthlyXAxis/MonthlyXAxis';

interface ActualVsPredictedProps {
  data: TrainTestData[];
  dateGrouping: string;
  legendValue: string;
  syncId?: number;
  tooltipFormatter?: (value: number) => string;
  yAxisFormatter?: (value: number) => string;
  xAxisTickFormatter?: (date: string) => string;
  xAxisTicks?: string[];
  yAxisStaticWidth?: number;
  setChartYAxisDynamicWidth?: (width: number) => void;
  shouldSortTooltipItems?: boolean;
  canUseMonthlyTicks?: boolean;
  tooltipProps?: TooltipPassthroughProps;
}

const RIGHT_MARGIN = 30;
const TOP_MARGIN = 20;

interface ActualVsPredictedTooltipContentProps {
  dateGrouping: string;
  tooltipFormatter?: (value: number) => string;
  shouldSortTooltipItems: boolean;
  tooltipProps: TooltipPassthroughProps;
  // recharts injects these when it clones the content element.
  label?: string | number;
  payload?: TooltipPayloadEntry[];
}

/**
 * Actual-vs-Predicted tooltip body. Shared by this component and the rMMM ModelOverview
 * chart (`@Pages/MMM/ModelResults/ActualVsPredicted`) — both rendered byte-identical tooltips.
 * Self-contained (reads `fiscal_cal` itself) so recharts can clone it directly as `content`.
 */
export const ActualVsPredictedTooltipContent = ({
  dateGrouping,
  tooltipFormatter,
  shouldSortTooltipItems,
  tooltipProps,
  label,
  payload
}: ActualVsPredictedTooltipContentProps) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  if (!payload || !label || payload.length === 0) return null;
  const date = payload[0].payload?.date;
  const formattedLabel = headerFormatter(dateGrouping, date, fiscalCal);
  let items = payload.map(item => {
    const isActual =
      item?.name && typeof item.name === 'string' && item.name.toLowerCase().includes('actual');

    return {
      label: (item?.name ?? '')
        .toString()
        .split('_')
        .map(capitalize)
        .join(' ')
        .replace('Kpi', 'KPI'),
      value: tooltipFormatter ? tooltipFormatter(item.value as number) : (item.value as number),
      color: item.stroke,
      keyShape: isActual ? KeyShapeOptions.SQUARE : KeyShapeOptions.LINE
    };
  });

  if (shouldSortTooltipItems) {
    items = items.sort((a, b) => -Math.abs(Number(a.value)) + Math.abs(Number(b.value)));
  }

  return <ChartTooltipV2 headers={[formattedLabel]} items={items} {...tooltipProps} />;
};

const ActualVsPredicted = ({
  data,
  dateGrouping,
  legendValue,
  syncId,
  tooltipFormatter,
  yAxisFormatter,
  xAxisTickFormatter,
  xAxisTicks,
  yAxisStaticWidth,
  setChartYAxisDynamicWidth,
  shouldSortTooltipItems = true,
  canUseMonthlyTicks = false,
  tooltipProps = {}
}: ActualVsPredictedProps): ReactElement => {
  const [trainAreaWidth, setTrainAreaWidth] = useState(0);
  const [renderedChartWidth, setRenderedChartWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data
  });

  useEffect(() => {
    if (setChartYAxisDynamicWidth) {
      setChartYAxisDynamicWidth(yAxisWidth);
    }
  }, [setChartYAxisDynamicWidth, yAxisWidth]);

  const dataWithTimestamps = useMemo(
    () =>
      data.map(row => ({
        ...row,
        timestamp: dateStringToUtcTimestamp(toBpmDateString(row?.date))
      })),
    [data]
  );

  const trainEnd =
    dataWithTimestamps[dataWithTimestamps.findIndex(d => d.train_test === 'test') - 1];

  // Drive the monthly-vs-fallback x-axis decision from the AutoSizer-measured chart width
  // rather than a DOM measurement of the rendered axis. The latter races with recharts v3's
  // asynchronous rendering, which caused the axis to intermittently stay on the fallback ticks.
  const xAxisPlotWidth = Math.max(
    0,
    renderedChartWidth - (yAxisStaticWidth || yAxisWidth) - RIGHT_MARGIN
  );

  const { shouldRenderFallback: shouldRenderFallbackAxis, component: monthlyXAxis } =
    useBuildMonthlyXAxis({
      data: dataWithTimestamps,
      yAxisWidth: yAxisStaticWidth || yAxisWidth,
      axisWidth: xAxisPlotWidth
    });
  const shouldRenderMonthlyTimestamps = canUseMonthlyTicks && !shouldRenderFallbackAxis;

  const defaultXAxis = (
    <XAxis
      dataKey='date'
      tick={TICK_STYLE}
      tickFormatter={val => (xAxisTickFormatter ? xAxisTickFormatter(val) : dateToMddyy(val))}
      ticks={xAxisTicks}
      interval={xAxisTicks ? 0 : 'preserveEnd'}
    />
  );

  const xAxisComponent = shouldRenderMonthlyTimestamps ? monthlyXAxis : defaultXAxis;

  const referenceX2 = useMemo(
    () => (shouldRenderMonthlyTimestamps ? trainEnd?.timestamp : trainEnd?.date),
    [shouldRenderMonthlyTimestamps, trainEnd?.date, trainEnd?.timestamp]
  );

  // Remeasure the rendered train region after every chart layout (initial render + resize)
  // so the Train/Test labels stay aligned over their respective chart areas.
  useLayoutEffect(() => {
    const trainArea = containerRef.current?.querySelector<SVGPathElement>(
      '.recharts-wrapper .recharts-surface .recharts-reference-area path'
    );
    if (trainArea) {
      setTrainAreaWidth(Number(trainArea.getAttribute('width')) || 0);
    }
  }, [renderedChartWidth, yAxisWidth, referenceX2, dataWithTimestamps]);

  // Seed the chart width before first paint so the correct (monthly vs fallback) x-axis is
  // chosen on the initial render instead of flashing the fallback ticks for a frame.
  useLayoutEffect(() => {
    const width = containerRef.current?.getBoundingClientRect().width;
    if (width) {
      setRenderedChartWidth(prev => (prev === width ? prev : width));
    }
  }, [data]);

  return (
    <div className='actual-vs-predicted-wrapper'>
      <div className='actual-vs-predicted-graph' ref={containerRef}>
        <div
          className='act-v-pred-title'
          style={{ marginLeft: yAxisStaticWidth || yAxisWidth, marginRight: RIGHT_MARGIN }}
        >
          <div
            className='period-legend train-period'
            style={{ width: trainAreaWidth ? trainAreaWidth - 32 : '100%' }}
          >
            Train Period
            <div style={{ backgroundColor: Brand20 }} className='square' />
          </div>
          <div className='period-legend test-period'>Test Period</div>
        </div>
        <AutoSizer
          className='actual-vs-predicted-auto-sizer'
          renderProp={({ width: chartWidth, height: chartHeight }) => {
            if (chartWidth && chartWidth !== renderedChartWidth) {
              setRenderedChartWidth(chartWidth);
            }
            return (
              <ComposedChart
                data={dataWithTimestamps}
                height={chartHeight ?? 0}
                margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
                syncId={syncId}
                ref={setChartRef}
                width={chartWidth ?? 0}
              >
                {xAxisComponent}
                <YAxis
                  tick={TICK_STYLE}
                  tickFormatter={yAxisFormatter}
                  tickLine={false}
                  axisLine={false}
                  width={yAxisStaticWidth || yAxisWidth}
                />
                <ReferenceArea fill={Brand20} x2={referenceX2} />
                <Area
                  dataKey='actual_kpi'
                  fill={Neutral400}
                  isAnimationActive={false}
                  stroke={Neutral400}
                  fillOpacity={1}
                />
                <Line
                  dataKey='predicted_kpi'
                  dot={false}
                  isAnimationActive={false}
                  stroke={Brand60}
                  strokeWidth={3}
                  type='monotone'
                />
                <ReferenceLine stroke={Neutral500} x={referenceX2} />
                <CartesianGrid
                  stroke={Neutral500}
                  strokeOpacity={0.25}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  vertical={false}
                />
                <Tooltip
                  content={
                    <ActualVsPredictedTooltipContent
                      dateGrouping={dateGrouping}
                      tooltipFormatter={tooltipFormatter}
                      shouldSortTooltipItems={shouldSortTooltipItems}
                      tooltipProps={tooltipProps}
                    />
                  }
                  cursor={{ stroke: Neutral500, width: '1px' }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            );
          }}
        />
      </div>
      <div className='right-of-chart'>
        <div className='legend'>
          <div className='legend-item'>
            <div className='square' style={{ backgroundColor: Neutral400 }} />
            <div>{`Actual ${legendValue}`}</div>
          </div>
          <div className='legend-item'>
            <div className='line' />
            <div>{`Predicted ${legendValue}`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ActualVsPredicted };
